/**
 * AD-021's `H` binding and its hold guard — **the port's first timed-hold
 * key**, which is why it is its own file rather than another block in
 * `keys.ts`.
 *
 * Every other binding there is one of two shapes: a member of an `AxisBinding`
 * or a key-list read out of the held set, or a one-shot in `DISCRETE_COMMANDS`.
 * This is neither. It carries an accumulator across frames, and the two things
 * that read it (`scene-loop.ts` for the jump, the HUD for the progress) are not
 * the things that read the rest of the map. Splitting it out also keeps
 * `keys.ts` under the size ceiling it was five lines from when this arrived —
 * `lessons.md`'s *"the size ceiling is not compositional"*.
 *
 * `keys.ts` still owns the *binding* half: `BAIL_OUT_KEY` is in its
 * `FLIGHT_KEYS` set (so the page cannot scroll under a held `H`) and its
 * `CONTROL_BINDINGS` row (so the hint panel prints it).
 */

import { clamp } from "@red-opus/engine";

/**
 * `H` — *hit the silk*.
 *
 * **Free in the original's key map *and* unused here**, which almost nothing
 * left is: the Quick Reference Card's free list (transcribed in
 * docs/game/player-bombing.md § *The original's key map*) is `B E G H I K L N O
 * Q R T V X Y Z 0`, and this port has since taken all of those but `H I L N Y
 * 0`. So `H` is chosen on the same standard `V` was — free in both maps — and
 * on the only mnemonic in that set: "hit the silk" is the period idiom for
 * going over the side, which `J` for *jump* would have matched too except that
 * `J` is FLIGHT LEADER COMMANDS' *join* on the card.
 */
export const BAIL_OUT_KEY = "h";

/**
 * How long `H` must be held before the pilot goes over the side.
 *
 * A second and a half: long enough that no brush of the key does it — and `H`
 * is `G`'s neighbour on a QWERTY board, the exact key a hand reaching for the
 * glance row brushes — and short enough to be usable with a wing on fire, which
 * is the case the feature exists for. There is no confirmation dialog: a modal
 * over a burning aeroplane is worse than a long press, and the sim does not stop
 * for one.
 */
export const BAIL_OUT_HOLD_SECONDS = 1.5;

/**
 * Advances the hold accumulator by one frame and returns its new value.
 *
 * Deliberately the same shape as `keys.ts`'s `advanceThrottle`: **pure**, with
 * the caller owning the number across frames, so the guard is unit-testable
 * without a DOM and without module state — the property that whole file is
 * built on.
 *
 * Releasing the key resets to zero. A hold has to be *continuous*, so tapping
 * `H` fifteen times does not add up to a press (`bail-out.test.ts` drums it).
 */
export function advanceBailHold(current: number, pressed: ReadonlySet<string>, dt: number): number {
  return pressed.has(BAIL_OUT_KEY) ? current + dt : 0;
}

/**
 * True on the frame the hold completes, and only that frame.
 *
 * This is what makes one long hold *one* jump rather than one per frame
 * thereafter — the rising edge, taken against the previous value rather than
 * latched in a flag the caller would have to clear.
 */
export function bailHoldCompleted(previous: number, current: number): boolean {
  return previous < BAIL_OUT_HOLD_SECONDS && current >= BAIL_OUT_HOLD_SECONDS;
}

/** 0..1, for the on-screen progress a player needs to see the press registering. */
export function bailHoldFraction(held: number): number {
  return clamp(held / BAIL_OUT_HOLD_SECONDS, 0, 1);
}
